// Focus and signal helpers shared by the image viewer and the video viewer.

/**
 * Hands the focus back to the element that opened a closing layer (floating
 * things close only after the focus is back home). **Only when
 * the focus is vacant**: if something else already took it (a dialog opened
 * from the layer), it is not taken back.
 *
 * "Vacant" means the focus is on nothing, on body, or inside the closing layer.
 * The layer may already be out of the DOM when cleanup runs, so focus on body
 * counts too.
 */
export function returnFocusTo(closing: HTMLElement, opener: Element | null) {
  // **Still in the DOM means not closed.** StrictMode (dev) runs cleanup and
  // mount once more right after mounting; returning the focus there would
  // pull it out of the layer the moment it opened.
  if (closing.isConnected) return;
  const active = document.activeElement;
  const vacant =
    active === null || active === document.body || closing.contains(active);
  if (!vacant) return;
  if (opener instanceof HTMLElement && opener.isConnected)
    opener.focus({ preventScroll: true });
}

// Focus returned after a viewer closed shows no ring (media.css) nor tooltip.
export { quietFocus } from "../../../ui/quiet-focus";

/** Follows a link (download, new tab) as if an `<a>` was clicked. */
export function follow(href: string, newTab: boolean): void {
  const a = document.createElement("a");
  a.href = href;
  if (newTab) {
    a.target = "_blank";
    a.rel = "noopener";
  }
  a.click();
}

/**
 * Lets exactly one host take one open signal.
 *
 * Several hosts may listen at once (one `MediaLayers` per document screen, and
 * more if another screen mounts its own), all on window. Without a mark one
 * click would open the same viewer once per host, stacked. Window listeners run
 * in order, synchronously, so the first host notes the event here and the rest
 * pass. Whichever host draws, the result looks the same (full screen, fixed).
 *
 * This module-level value is the one unavoidable piece of shared state: hosts
 * are independent components and the signal is a DOM event.
 */
const claimed = new WeakSet<Event>();

export function claim(e: Event): boolean {
  if (claimed.has(e)) return false;
  claimed.add(e);
  return true;
}
